import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Receipt, Search } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { EmptyState, Panel, StatusBadge, DetailDrawer, formatDate } from "@/components/admin/ui";
import { BillingDetail } from "@/components/billing/BillingDetail";
import QuoteDrawer from "@/components/admin/QuoteDrawer";
import { usePolling } from "@/hooks/usePolling";
import { usePageLastUpdated } from "@/context/PageMetaContext";

export default function AdminBilling() {
  const { API } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [records, setRecords] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [statuses, setStatuses] = useState([]);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [status, setStatus] = useState(searchParams.get("status") || "");
  const [focusOrgId, setFocusOrgId] = useState(searchParams.get("focus") || null);
  const [detail, setDetail] = useState(null);
  const [quoteDrawerId, setQuoteDrawerId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    if (!silent) setError("");
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (status) params.status = status;
      const { data } = await axios.get(`${API}/admin/billing`, { params, withCredentials: true });
      setRecords(data.records || []);
      setStatusCounts(data.status_counts || {});
      setStatuses(data.statuses || []);
      setLastUpdated(Date.now());
    } catch (err) {
      if (!silent) setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [API, search, status]);

  useEffect(() => {
    const timer = window.setTimeout(load, search ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [load, search]);

  usePolling(() => load(true), 10000);

  useEffect(() => {
    const params = {};
    if (search) params.search = search;
    if (status) params.status = status;
    if (focusOrgId) params.focus = focusOrgId;
    setSearchParams(params, { replace: true });
  }, [search, status, focusOrgId, setSearchParams]);

  const openDetail = async (orgId) => {
    setFocusOrgId(orgId);
    try {
      const { data } = await axios.get(`${API}/admin/organizations/${orgId}`, { withCredentials: true });
      setDetail(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const closeDetail = () => { setFocusOrgId(null); setDetail(null); };

  usePageLastUpdated(lastUpdated, "billing-last-updated");

  return (
    <div className="space-y-6" data-testid="admin-billing-page">
      <Panel testId="billing-table-panel">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2" data-testid="billing-status-tabs">
            {["All", ...statuses].map((item) => {
              const value = item === "All" ? "" : item;
              const active = status === value;
              return (
                <button
                  key={item}
                  type="button"
                  onClick={() => setStatus(value)}
                  data-testid={`billing-filter-${item.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`rounded-full px-4 py-2 text-sm font-bold transition-colors duration-200 ${active ? "bg-[#0052FF] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"}`}
                >
                  {item}{item !== "All" ? ` (${statusCounts[item] || 0})` : ""}
                </button>
              );
            })}
          </div>
          <label className="relative block">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="billing-search-input"
              placeholder="Search organization, quote ID…"
              className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-4 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 sm:w-72 dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500"
            />
          </label>
        </div>

        {error && <p className="mt-5 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="billing-error">{error}</p>}

        <div className="mt-6">
          {loading ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-10 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400" data-testid="billing-loading">Loading billing records…</div>
          ) : records.length === 0 ? (
            <EmptyState icon={Receipt} title="No billing records yet." message="Billing relationships appear here once a quote is accepted and an organization is created." testId="billing-empty" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1000px] text-left text-sm" data-testid="admin-billing-list">
                <thead>
                  <tr className="border-b border-slate-200 text-xs uppercase tracking-[0.12em] text-slate-400 dark:border-white/10 dark:text-slate-500">
                    <th className="py-3 pr-4 font-bold">Organization</th>
                    <th className="py-3 pr-4 font-bold">Quote ID</th>
                    <th className="py-3 pr-4 font-bold">Plan / Solutions</th>
                    <th className="py-3 pr-4 font-bold">Billing Frequency</th>
                    <th className="py-3 pr-4 font-bold">Next Payment</th>
                    <th className="py-3 pr-4 font-bold">Billing Status</th>
                    <th className="py-3 font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r) => (
                    <tr key={r.org_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 dark:border-white/5 dark:hover:bg-white/5" data-testid={`billing-row-${r.org_id}`}>
                      <td className="py-4 pr-4 font-bold text-slate-950 dark:text-white">{r.org_name}</td>
                      <td className="py-4 pr-4">
                        {r.quote_id ? (
                          <button type="button" onClick={() => setQuoteDrawerId(r.quote_id)} data-testid={`billing-quote-link-${r.org_id}`} className="font-bold text-blue-600 hover:underline dark:text-blue-400">{r.quote_id}</button>
                        ) : <span className="text-slate-400 dark:text-slate-500">—</span>}
                      </td>
                      <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">
                        {r.solution_items.length === 0 ? "—" : r.solution_items.length === 1 ? r.solution_items[0].description : `${r.solution_items[0].description} +${r.solution_items.length - 1} more`}
                      </td>
                      <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">{r.billing_frequency || "—"}</td>
                      <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">{formatDate(r.next_billing_date)}</td>
                      <td className="py-4 pr-4"><StatusBadge status={r.payment_status} testId={`billing-row-status-${r.org_id}`} /></td>
                      <td className="py-4">
                        <button type="button" onClick={() => openDetail(r.org_id)} data-testid={`billing-view-${r.org_id}`} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-white dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Panel>

      <DetailDrawer open={!!focusOrgId} onClose={closeDetail} title={detail?.organization?.name || "Billing"} subtitle="Nova Opsis billing relationship" testId="billing-detail-drawer" wide>
        {detail && (
          <BillingDetail
            orgName={detail.organization.name}
            billing={detail.organization.billing || {}}
            quote={detail.quote}
            solutionItems={detail.organization.solution_items || []}
            onViewQuote={() => setQuoteDrawerId(detail.organization.billing?.quote_id)}
          />
        )}
      </DetailDrawer>

      <QuoteDrawer quoteId={quoteDrawerId} open={!!quoteDrawerId} onClose={() => setQuoteDrawerId(null)} />
    </div>
  );
}
